<template>
  <div class="edit-dialog" v-if="isVisible && assetForm">
    <el-dialog
      :title="
        $t('dialogs.defaultEdit.title', {
          asset: $tc(`labels.${assetType}`, 1)
        })
      "
      :visible="isVisible"
      :close-on-click-modal="false"
      @close="$emit('update-edit-dialog-visibility', false)"
      :width="windowWidth < 1024 ? '95%' : '920px'"
    >
      <div class="edit-asset-values">
        <span>
          <b>ID</b>
        </span>
        <span class="auto-phrase">
          <b>{{ $t("dialogs.defaultEdit.currentBattery") }}</b>
        </span>
        <span>
          <b>{{ $t("labels.lastSeen") }}</b>
        </span>
      </div>
      <div class="edit-asset-values">
        <span v-if="assetForm.tagId">
          {{
            assetForm.tagId
              .toString(16)
              .toUpperCase()
              .match(/.{1,2}/g)
              .join("")
          }}
        </span>
        <span>
          <TempBatteryIcon
            :battery="assetForm.battery"
            :width="26"
            :height="14"
          />
        </span>
        <span
          v-if="assetForm.lastSeen"
          :style="{ color: computedColor(assetPrevInfo) }"
        >
          {{
            moment.utc(assetForm.lastSeen * 1000).format("MMM DD YYYY, HH:mm")
          }}
        </span>
      </div>
      <el-form
        label-position="right"
        :model="assetForm"
        :rules="rules"
        ref="assetForm"
        hide-required-asterisk
        @submit.native.prevent
        class="edit-asset-form"
      >
        <template
          v-if="
            assetPrevInfo.type === 1 && !assetPrevInfo.active && isClientDefault
          "
        >
          <div class="warning-container yellow">
            <i class="el-icon-warning" />
            <span>{{ $t("messages.tagInactive") }}</span>
          </div>
        </template>
        <template
          v-else-if="
            assetPrevInfo.type === 0 && !assetForm.active && isClientDefault
          "
        >
          <div class="warning-container red">
            <i class="el-icon-warning" />
            <span>{{ $t("messages.installBeaconToEdit") }}.</span>
          </div>
          <div class="redirect-button-container">
            <el-button
              size="mini"
              @click="redirectToDefineLocations()"
              icon="el-icon-location-outline"
            >
              {{ $t("actions.redirectToDefineLocations") }}
              <i class="el-iconright" />
            </el-button>
          </div>
        </template>
        <template v-else-if="Boolean(tagToReplace) && isClientDefault">
          <div class="warning-container yellow">
            <i class="el-icon-warning" />
            <span>{{ $t("messages.beingReplaceMessage") }}</span>
          </div>
        </template>
        <el-row :gutter="20">
          <el-col :xs="24" :sm="24" :md="12" :lg="12" :xl="12">
            <el-form-item prop="label">
              <el-row :gutter="10" type="flex" justify="start" align="center">
                <el-col :span="10">
                  <div class="form-item-label">
                    {{
                      $t("dialogs.defaultEdit.setToEdit", {
                        toEdit: $t("dialogs.defaultEdit.label")
                      })
                    }}
                  </div>
                </el-col>

                <el-col :span="14">
                  <el-input
                    type="text"
                    v-model="assetForm.label"
                    autocomplete="off"
                    @keyup.enter.native="validateForm(true)"
                    :disabled="
                      ((assetPrevInfo.type === 0 && !assetForm.active) ||
                        Boolean(tagToReplace)) &&
                        isClientDefault
                    "
                  />
                </el-col>
              </el-row>
            </el-form-item>
            <el-form-item class="form-item-lg-margin">
              <el-row :gutter="10" type="flex" justify="start" align="center">
                <el-col :span="10">
                  <div class="form-item-label">
                    {{
                      $t("dialogs.defaultEdit.setToEdit", {
                        toEdit: $t("dialogs.defaultEdit.range")
                      })
                    }}
                  </div>
                </el-col>

                <el-col :span="14">
                  <div class="slider-col">
                    <el-slider
                      v-model="assetForm.threshold"
                      :marks="marksNonDefault"
                      :step="1"
                      :min="0"
                      :max="16"
                      :disabled="
                        ((assetPrevInfo.type === 0 && !assetForm.active) ||
                          Boolean(tagToReplace)) &&
                          isClientDefault
                      "
                    >
                    </el-slider>
                  </div>
                </el-col>
              </el-row>
            </el-form-item>

            <el-form-item v-if="isClientDefault">
              <el-row :gutter="10" type="flex" justify="start" align="center">
                <el-col :span="10">
                  <div class="form-item-label">
                    {{
                      $t("dialogs.defaultEdit.toEditAsset", {
                        asset: $tc(`labels.${assetType}`, 1),
                        toEdit: $t("dialogs.defaultEdit.replace")
                      })
                    }}
                  </div>
                </el-col>

                <el-col :span="14">
                  <el-select
                    v-model="tagToReplace"
                    :placeholder="
                      assetType === 'beacon'
                        ? $t('placeholder.selectBeacon')
                        : $tc('placeholder.selectTag', 1)
                    "
                    filterable
                    class="select-tags"
                    clearable
                    :disabled="
                      assetPrevInfo.type === 0 &&
                        !assetForm.active &&
                        isClientDefault
                    "
                    @clear="tagToReplace = null"
                  >
                    <el-option
                      v-for="tag in inactiveTags"
                      :key="`tag.label${tag.id}`"
                      :label="
                        tag.tagId
                          ? `(${tag.tagId
                              .toString(16)
                              .toUpperCase()
                              .match(/.{1,2}/g)
                              .join('')}) ${tag.label}`
                          : ''
                      "
                      :value="tag.id"
                    ></el-option>
                  </el-select>
                </el-col>
              </el-row>
            </el-form-item>
            <el-form-item v-if="isTracker && assetPrevInfo.type === 0">
              <el-row :gutter="10" type="flex" justify="start" align="center">
                <el-col :span="10">
                  <div class="form-item-label">
                    {{
                      $t("dialogs.defaultEdit.toEditAsset", {
                        asset: $tc(`labels.${assetType}`, 1),
                        toEdit: $t("dialogs.defaultEdit.status")
                      })
                    }}
                  </div>
                </el-col>

                <el-col :span="14">
                  <el-select
                    v-model="assetForm.status"
                    :placeholder="$t('placeholder.selectStatus')"
                    filterable
                    class="select-status"
                    :disabled="
                      ((assetPrevInfo.type === 0 && !assetForm.active) ||
                        Boolean(tagToReplace)) &&
                        isClientDefault
                    "
                  >
                    <el-option
                      v-for="status in statusOptions"
                      :key="`tag.status-${status}`"
                      :label="status"
                      :value="status"
                    ></el-option>
                  </el-select>
                </el-col>
              </el-row>
            </el-form-item>
            <el-form-item v-if="isClientDefault || isClientIM">
              <el-row :gutter="10" type="flex" justify="start" align="center">
                <el-col :span="10">
                  <div class="form-item-label">
                    {{
                      $t("dialogs.defaultEdit.setToEdit", {
                        toEdit: $t("dialogs.defaultEdit.state")
                      })
                    }}
                  </div>
                </el-col>
                <el-col :span="14" class="test-class">
                  <div class="checkbox-col">
                    <el-switch
                      class="switch"
                      v-model="assetForm.active"
                      active-color="#1F4258"
                      inactive-color="#1F4258"
                      :active-text="
                        assetPrevInfo.type === 1
                          ? $t('labels.active')
                          : $t('labels.installed')
                      "
                      :inactive-text="$t('labels.inactive')"
                      :disabled="
                        ((assetPrevInfo.type === 0 && !assetForm.active) ||
                          Boolean(tagToReplace)) &&
                          isClientDefault
                      "
                    >
                    </el-switch>
                    <!-- <el-checkbox
                      v-model="assetForm.active"
                      size="small"
                      @change="onActiveStatusChange"
                      >{{
                        assetPrevInfo.type === 0
                          ? $t("labels.installed")
                          : $t("labels.active")
                      }}</el-checkbox
                    > -->
                  </div>
                </el-col>
              </el-row>
            </el-form-item>
            <el-form-item v-if="isAzitek">
              <el-row :gutter="10" type="flex" justify="start" align="center">
                <el-col :span="10">
                  <div class="form-item-label" style="color: #2986cc">
                    {{
                      $t("dialogs.defaultEdit.toEditAsset", {
                        asset: $tc(`labels.${assetType}`, 1),
                        toEdit: $t("dialogs.defaultEdit.destroy")
                      })
                    }}
                  </div>
                </el-col>
                <el-col :span="14">
                  <div class="checkbox-col">
                    <el-checkbox
                      v-model="assetForm.destroy"
                      size="small"
                      :disabled="
                        ((assetPrevInfo.type === 0 && !assetForm.active) ||
                          Boolean(tagToReplace)) &&
                          isClientDefault
                      "
                      >{{
                        assetForm.destroy
                          ? $t("dialogs.defaultEdit.destroy")
                          : ""
                      }}</el-checkbox
                    >
                  </div>
                </el-col>
              </el-row>
            </el-form-item>
          </el-col>

          <el-col :xs="24" :sm="24" :md="12" :lg="12" :xl="12">
            <el-form-item v-if="isTracker && assetPrevInfo.type === 1">
              <el-row :gutter="10" type="flex" justify="start" align="center">
                <el-col :span="10">
                  <div class="form-item-label">
                    {{ $t("dialogs.defaultEdit.associatedBeacon") }}
                  </div>
                </el-col>
                <el-col :span="14">
                  <el-select
                    v-model="assetForm.beaconId"
                    clearable
                    :disabled="
                      ((assetPrevInfo.type === 0 && !assetForm.active) ||
                        Boolean(tagToReplace)) &&
                        isClientDefault
                    "
                  >
                    <el-option
                      v-for="item in resources.data.tags.filter(
                        tag => tag.type === 0 && tag.active
                      )"
                      :key="item.id"
                      :label="`${item.label} (${toHex(item.tagId)})`"
                      :value="item.tagId"
                    >
                    </el-option>
                  </el-select>
                </el-col>
              </el-row>
            </el-form-item>
            <el-form-item v-if="isTracker && assetPrevInfo.type === 1">
              <el-row :gutter="10" type="flex" justify="start" align="center">
                <el-col :span="10">
                  <div class="form-item-label">
                    {{
                      $t("dialogs.defaultEdit.toEditAsset", {
                        asset: $tc(`labels.${assetType}`, 1),
                        toEdit: $t("dialogs.defaultEdit.status")
                      })
                    }}
                  </div>
                </el-col>

                <el-col :span="14">
                  <el-select
                    v-model="assetForm.status"
                    :placeholder="$t('placeholder.selectStatus')"
                    filterable
                    class="select-status"
                    :disabled="
                      ((assetPrevInfo.type === 0 && !assetForm.active) ||
                        Boolean(tagToReplace)) &&
                        isClientDefault
                    "
                  >
                    <el-option
                      v-for="status in statusOptions"
                      :key="`tag.status-${status}`"
                      :label="status"
                      :value="status"
                    ></el-option>
                  </el-select>
                </el-col>
              </el-row>
            </el-form-item>
          </el-col>

          <el-col :xs="24" :sm="24" :md="12" :lg="12" :xl="12">
            <el-form-item v-if="isClientDefault && assetPrevInfo.type === 0">
              <el-row :gutter="8" type="flex" justify="start" align="center">
                <el-col :span="10">
                  <div class="form-item-label">
                    {{ $t("labels.childBeacons") }}
                  </div>
                </el-col>
                <el-col :xs="12" :sm="16" :md="16" :lg="16" :xl="16">
                  <el-tooltip
                    class="item"
                    effect="dark"
                    :content="$t('tooltip.childBeaconsShare')"
                    placement="right"
                  >
                    <el-select
                      v-model="childBeaconToAdd"
                      :placeholder="$t('placeholder.selectBeacon')"
                      filterable
                      clearable
                      :disabled="
                        ((assetPrevInfo.type === 0 && !assetForm.active) ||
                          Boolean(tagToReplace)) &&
                          isClientDefault
                      "
                    >
                      <el-option
                        v-for="tag in orphanTags"
                        :key="`tag.label${tag.id}`"
                        :label="
                          tag.tagId
                            ? `(${tag.tagId
                                .toString(16)
                                .toUpperCase()
                                .match(/.{1,2}/g)
                                .join('')}) ${tag.label}`
                            : ''
                        "
                        :value="tag.tagId"
                      ></el-option>
                    </el-select>
                  </el-tooltip>
                </el-col>
                <el-col :span="2">
                  <el-button
                    type="primary"
                    size="small"
                    icon="el-icon-plus"
                    circle
                    @click="addTagToGroup"
                    :disabled="
                      ((assetPrevInfo.type === 0 && !assetForm.active) ||
                        Boolean(tagToReplace)) &&
                        isClientDefault
                    "
                  />
                </el-col>
              </el-row>
            </el-form-item>
            <div>
              <div v-for="tag in selectedChildBeacons" :key="tag.id">
                <el-row :gutter="12" class="child-beacon-list">
                  <el-col :span="16" :offset="8" class="background-animation">
                    <span class="child-beacon-list__tagId">
                      {{
                        tag.id
                          ? `${tag.id
                              .toString(16)
                              .toUpperCase()
                              .match(/.{1,2}/g)
                              .join("")} (${tag.label})`
                          : ""
                      }}
                    </span>
                    <el-button
                      size="small"
                      type="danger"
                      class="el-icon-delete"
                      circle
                      @click="removeTagFromSelected(tag)"
                      :disabled="
                        ((assetPrevInfo.type === 0 && !assetForm.active) ||
                          Boolean(tagToReplace)) &&
                          isClientDefault
                      "
                    />
                  </el-col>
                </el-row>
              </div>
              <div v-if="assetForm.childTags?.length">
                <div
                  class="edit-asset-child-tags"
                  v-for="tag in assetForm.childTags"
                  :key="tag.id"
                >
                  <el-row :gutter="12" class="child-beacon-list">
                    <el-col class="label-div" :span="12" :offset="8">
                      <span class="child-beacon-list__id">
                        {{
                          tag.id
                            ? `${tag.id
                                .toString(16)
                                .toUpperCase()
                                .match(/.{1,2}/g)
                                .join("")} (${tag.label})`
                            : ""
                        }}
                      </span>
                    </el-col>
                    <el-col :span="2" class="child-beacon-list__delete">
                      <el-button
                        size="small"
                        type="danger"
                        class="el-icon-delete"
                        circle
                        @click="removeTag(tag)"
                      />
                    </el-col>
                  </el-row>
                </div>
              </div>
            </div>

            <div
              v-if="assetForm.error?.length"
              class="el-form-item__error el-form-item__error--server"
            >
              {{ assetForm.error }}
            </div>
          </el-col>
        </el-row>
      </el-form>
      <span slot="footer" class="dialog-footer" style="margin-top: -10%">
        <el-button @click="confirmCancel" :disabled="loadingStatus">{{
          $t("button.cancel")
        }}</el-button>
        <el-button
          type="primary"
          @click="validateForm(false)"
          :loading="loadingStatus"
          :disabled="
            assetPrevInfo.type === 0 && !assetForm.active && isClientDefault
          "
          >{{ $t("button.save") }}</el-button
        >
      </span>
    </el-dialog>
    <SaveChangesDialog
      :isVisible="showCertifySave"
      @cancel-save="showCertifySave = false"
      @confirm-save="finalSaveTag"
    />
    <el-dialog
      :title="$t('dialogs.discardChanges.title')"
      :visible.sync="showCertifyDiscard"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      class="discard-changes-dialog"
    >
      <span class="warning"
        >{{ $t("dialogs.discardChanges.line1") }} <br />
        {{ $t("dialogs.discardChanges.line2") }} <br />
      </span>
      <span slot="footer" class="dialog-footer">
        <el-button @click="showCertifyDiscard = false" size="small">{{
          $t("button.continueEditing")
        }}</el-button>
        <el-button @click="discardChanges" type="danger" size="small">{{
          $t("button.discard")
        }}</el-button>
      </span>
    </el-dialog>
    <el-dialog
      :title="$t('dialogs.attention.title')"
      width="400px"
      :visible.sync="showAssociatedRoutes"
      :close-on-click-modal="false"
      class="associated-routes-dialog"
    >
      <div class="dialog-info">
        {{ $t("dialogs.attention.line1") }}
      </div>
      <div v-for="route in routesAssociatedToTag" :key="route.id">
        <span>{{ route.label }}</span>
      </div>
    </el-dialog>
    <el-dialog
      class="remove-from-map-dialog"
      width="400px !important"
      :title="$t('actions.removeFromMap')"
      :visible.sync="removeFromMapDialog"
      :close-on-click-modal="false"
      v-if="assetPrevInfo && removeFromMapDialog"
    >
      <span class="dialog-info"
        >{{ $t("dialogs.activateTag.beaconRemoveWarning") }}<br />
      </span>
      <span slot="footer" class="dialog-footer">
        <el-button
          @click="cancelRemoveFromMap()"
          size="small"
          class="footer-button"
          >{{ $t("button.cancel") }}</el-button
        >
        <el-button
          type="primary"
          @click="updateTag(true)"
          size="small"
          class="submit-button footer-button"
          >{{ $t("button.save") }}</el-button
        >
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex";
import * as actionTypes from "@/store/action-types";
import tagsApi from "@/modules/tags/api/tags.api";
import routesApi from "@/modules/routes/api/routes.api";
import positionLogsApi from "@/modules/position-logs/api/position-logs.api";
import SaveChangesDialog from "@/components/SaveChangesDialog.vue";
import TempBatteryIcon from "./icons/TempBatteryIcon.vue";
import moment from "moment-timezone";

export default {
  name: "EditDialog",
  components: {
    TempBatteryIcon,
    SaveChangesDialog
  },

  props: {
    assetType: {
      type: String,
      default: "beacon"
    },
    rules: {
      type: Object,
      default: () => ({})
    },
    assetPrevInfo: {
      type: Object,
      default: () => ({})
    },
    isVisible: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      moment,
      marksNonDefault: {
        0: {
          style: {
            color: "#000000",
            width: "40px"
          },
          label: this.$t("labels.min")
        },
        16: {
          style: {
            color: "#000000",
            width: "40px"
          },
          label: this.$t("labels.max")
        }
      },
      statusOptions: ["NO STATUS", "NOK", "OK"],
      assetForm: {
        tagId: 0,
        label: "",
        active: false,
        threshold: null,
        battery: -1,
        lastSeen: -1,
        error: "",
        status: "NO STATUS",
        childTags: [],
        beaconId: null,
        destroy: false
      },
      tagToReplace: null,
      childBeaconToAdd: null,
      orphanTags: [],
      showCertifySave: false,
      selectedChildBeacons: [],
      tagsToDelete: [],
      tagsToSend: [],
      inactiveTags: [],
      loadingStatus: false,
      showAssociatedRoutes: false,
      routesAssociatedToTag: [],
      routeList: [],
      tempOrphanTags: [],
      parentInfo: {},
      showCertifyDiscard: false,
      windowWidth: window.innerWidth,
      removeFromMapDialog: false
    };
  },

  watch: {
    isVisible(newValue) {
      if (newValue && this.assetPrevInfo) {
        this.updateTempOrphanTags();
        this.orphanTags = this.tempOrphanTags.filter(
          t => t.id !== this.assetPrevInfo.id
        );
        this.inactiveTags = this.resources.data.tags.filter(
          tag => tag.type === this.assetPrevInfo.type && tag.active === false
        );
        this.parentInfo = this.resources.data.tags.find(elem => {
          return elem.id === this.assetPrevInfo.parentId;
        });
        this.assetForm.label = this.assetPrevInfo.label;
        this.assetForm.id = this.assetPrevInfo.id;
        this.assetForm.active = this.assetPrevInfo.active;
        this.assetForm.threshold = this.thresholdToRange(
          this.assetPrevInfo.threshold
        );
        this.assetForm.battery = this.assetPrevInfo.battery;
        this.assetForm.lastSeen = this.assetPrevInfo.lastUpdate;
        this.assetForm.tagId = this.assetPrevInfo.tagId;
        this.assetForm.childTags = this.assetPrevInfo.childTags;

        // Beacons
        if (this.isTracker) {
          this.assetForm.status = this.assetPrevInfo.status;
        }
        // Tags
        if (this.isTracker && this.assetPrevInfo.type === 1) {
          const isBeacon = this.resources.data.tags.find(
            tag => tag.tagId === this.assetPrevInfo.beaconId
          );
          this.assetForm.beaconId = isBeacon
            ? this.assetPrevInfo.beaconId
            : null;
        }
      } else {
        this.assetForm = {
          tagId: 0,
          label: "",
          active: false,
          threshold: null,
          battery: -1,
          lastSeen: -1,
          error: "",
          status: "NO STATUS",
          childTags: [],
          beaconId: null,
          destroy: false
        };
        this.selectedChildBeacons = [];
        this.tagsToSend = [];
        this.tagsToDelete = [];
        this.tagToReplace = null;
      }
    },
    "$i18n.locale"() {
      this.marksNonDefault = {
        0: {
          style: {
            color: "#000000",
            width: "40px"
          },
          label: this.$t("labels.min")
        },
        16: {
          style: {
            color: "#000000",
            width: "40px"
          },
          label: this.$t("labels.max")
        }
      };
    },
    "assetForm.label"(newValue) {
      if (this.assetForm.tagId && this.isClientDefault) {
        if (this.assetPrevInfo && this.assetPrevInfo.type === 1) {
          this.assetForm.active = Boolean(newValue);
        }
      }
    },
    "assetForm.active"(newValue) {
      if (this.assetForm.tagId && this.isClientDefault) {
        if (this.assetPrevInfo && this.assetPrevInfo.type === 1) {
          if (newValue) {
            if (!this.assetForm.label) {
              this.assetForm.label = this.toHex(this.assetForm.tagId);
            }
          } else {
            this.assetForm.label = "";
          }
        } else if (this.assetPrevInfo && this.assetPrevInfo.type === 0) {
          if (!newValue && this.assetPrevInfo.active !== newValue) {
            this.removeFromMapDialog = true;
          }
        }
      }
    }
  },

  computed: {
    ...mapState("user", {
      resources: "resources"
    }),
    ...mapState("clients", {
      clientAnchors: "anchors",
      clientData: "data"
    }),
    ...mapGetters("auth", ["isTracker", "isAzitek"]),
    ...mapGetters("clients", [
      "isClientDefault",
      "isClientDefaultNonTracker",
      "isClientIM"
    ])
  },

  methods: {
    ...mapActions("user", {
      userSetResourcesTags: actionTypes.USER_SET_RESOURCES_TAGS
    }),
    toHex(str) {
      return str
        .toString(16)
        .toUpperCase()
        .match(/.{1,2}/g)
        .join("");
    },
    thresholdToRange(threshold) {
      return threshold >= -16 ? 0 : -threshold - 16;
    },
    sortByLastUpdate(arr) {
      arr.sort((a, b) => {
        if (a.lastUpdate < b.lastUpdate) return 1;
        else return -1;
      });
    },
    isTagOutdated(tag) {
      const oneWeekAgo = Math.ceil(
        moment()
          .tz(this.clientData.timezone)
          .subtract(7, "days")
          .valueOf() / 1000
      );
      if (tag.lastUpdate < oneWeekAgo) return true;
      return false;
    },
    computedColor(tag) {
      if (this.isTagOutdated(tag)) return "red";
      else return "";
    },
    orderTags(tagList) {
      const noBatteryTags = tagList.filter(
        tag => tag.battery === 0 && tag.active
      );
      this.sortByLastUpdate(noBatteryTags);

      const tagsNotSeen = tagList.filter(
        tag => this.isTagOutdated(tag) && tag.battery != 0 && tag.active
      );
      this.sortByLastUpdate(tagsNotSeen);

      const badTags = [].concat(noBatteryTags).concat(tagsNotSeen);

      const restOfTags = tagList.filter(
        tag => tag.battery != 0 && badTags.indexOf(tag) === -1 && tag.active
      );
      this.sortByLastUpdate(restOfTags);

      const notActiveTags = tagList.filter(tag => !tag.active);
      this.sortByLastUpdate(notActiveTags);

      const sortedTagList = badTags.concat(restOfTags).concat(notActiveTags);

      this.userSetResourcesTags(sortedTagList);
    },
    async updateTagsList() {
      try {
        const { data } = await tagsApi.getRefreshStatus();
        const updatedTagList = data;
        updatedTagList.forEach(elem => {
          elem.childTags = [];
        });
        updatedTagList.forEach(element => {
          const index = updatedTagList.findIndex(
            elem => elem.id == element.parentId
          );
          if (index !== -1) {
            updatedTagList[index].childTags.push({
              id: element.tagId,
              label: element.label,
              parentId: element.parentId
            });
          }
        });
        this.orderTags(updatedTagList);
      } catch (_) {
        // DO nothing
      }
    },
    async updateTag(changeActiveState = false) {
      if (!changeActiveState) {
        this.loadingStatus = true;
      }

      try {
        const payload = {
          label: this.assetPrevInfo.label,
          active: this.assetForm.active,
          threshold: this.assetPrevInfo.threshold,
          childBeacons: [],
          beaconsToRemove: []
        };

        if (!changeActiveState) {
          if (this.tagToReplace && !changeActiveState) {
            await this.replaceTag(this.assetPrevInfo);
            return;
          }

          const threshold = -this.assetForm.threshold - 16;

          payload.label = this.assetForm.label;
          payload.active = this.assetForm.active;
          payload.threshold = threshold;
          payload.childBeacons = this.tagsToSend;
          payload.beaconsToRemove = this.tagsToDelete;

          // Updates status and beaconId only if changed
          if (this.isTracker) {
            if (this.assetForm.status !== this.assetPrevInfo.status) {
              payload.status = this.assetForm.status;
            }
            if (
              this.assetPrevInfo.type === 1 &&
              this.assetForm.beaconId !== this.assetPrevInfo.beaconId
            ) {
              const foundTag = this.resources.data.tags.find(
                tag => tag.tagId == this.assetForm.beaconId
              );
              payload.beaconId = foundTag ? foundTag.id : null;
            }
          }
        }

        const result = await tagsApi.updateTag(this.assetForm.tagId, payload);

        if (result.data) {
          this.updateTagsList();
          this.$emit("tag-updated", this.tagsToSend, this.tagsToDelete);
        }

        if (!changeActiveState) {
          this.assetForm = {
            tagId: 0,
            label: "",
            active: false,
            threshold: null,
            battery: -1,
            lastSeen: -1,
            error: "",
            status: "NO STATUS",
            childTags: [],
            beaconId: null,
            destroy: false
          };
          this.selectedChildBeacons = [];
          this.tagsToSend = [];
          this.tagsToDelete = [];
          this.$emit("update-edit-dialog-visibility", false);
        }
      } catch (err) {
        this.$notify.error({
          title: this.$t("statusMessages.error"),
          message: this.$t("messages.genericError")
        });
        if (changeActiveState) {
          this.assetForm.active = true;
        }
      } finally {
        this.loadingStatus = false;
        if (this.removeFromMapDialog) {
          this.removeFromMapDialog = false;
        }
      }
    },
    cancelRemoveFromMap() {
      this.assetForm.active = true;
      this.removeFromMapDialog = false;
    },
    async destroyTag() {
      try {
        const res = await tagsApi.updateTag(this.assetPrevInfo.tagId, {
          destroyed: true
        });

        const tagList = this.resources.data.tags;
        const index = tagList.findIndex(
          tag => tag.tagId === this.assetPrevInfo.tagId
        );
        const updatedTag = this.assetPrevInfo;

        if (index != -1) {
          updatedTag.destroyed = res.data.destroyed;
          tagList[index] = updatedTag;
          this.userSetResourcesTags(tagList);
          this.$emit("tag-destroyed", updatedTag.tagId, updatedTag.destroyed);
        }
        this.$notify({
          title: this.$t("statusMessages.success"),
          message: this.$t("messages.beaconDeleted"),
          type: "success"
        });
      } catch (err) {
        this.$notify.error({
          title: this.$t("statusMessages.error"),
          message: this.$t("messages.genericError")
        });
      } finally {
        console.log("deleted");
      }
    },
    saveTag() {
      if (!this.assetForm.destroy) {
        this.updateTag();
      } else {
        this.destroyTag();
      }
    },
    discardChanges() {
      this.showCertifyDiscard = false;
      this.$emit("update-edit-dialog-visibility", false);
    },
    confirmCancel() {
      const isBeacon = this.resources.data.tags.find(
        tag => tag.tagId === this.assetPrevInfo.beaconId
      );
      if (
        this.selectedChildBeacons.length != 0 ||
        this.tagToReplace ||
        (this.assetForm.beaconId != this.assetPrevInfo.beaconId && isBeacon)
      )
        this.showCertifyDiscard = true;
      else this.$emit("update-edit-dialog-visibility", false);
    },
    confirmSave() {
      const isBeacon = this.resources.data.tags.find(
        tag => tag.tagId === this.assetPrevInfo.beaconId
      );
      if (
        this.selectedChildBeacons.length != 0 ||
        this.tagToReplace ||
        (this.assetForm.beaconId != this.assetPrevInfo.beaconId && isBeacon)
      )
        this.showCertifySave = true;
      else this.saveTag();
    },
    finalSaveTag() {
      this.saveTag();
      this.showCertifySave = false;
    },
    validateForm(saveTag) {
      this.$refs.assetForm.validate(async valid => {
        if (valid) {
          if (saveTag) {
            this.saveTag();
          } else {
            this.confirmSave();
          }
        }
      });
    },
    addTagToGroup() {
      const index = this.resources.data.tags.findIndex(
        elem => elem.tagId === this.childBeaconToAdd
      );
      this.selectedChildBeacons.push({
        id: this.childBeaconToAdd,
        label: this.resources.data.tags[index].label,
        parentId: this.assetPrevInfo.id
      });
      this.tagsToSend.push(this.childBeaconToAdd);
      this.orphanTags = this.orphanTags.filter(
        elem => elem.tagId !== this.childBeaconToAdd
      );
      this.childBeaconToAdd = null;
    },
    removeTagFromSelected(tag) {
      this.selectedChildBeacons = this.selectedChildBeacons.filter(
        elem => elem.id !== tag.id
      );
      this.tagsToSend = this.tagsToSend.filter(elem => elem !== tag.id);
      this.orphanTags.push(
        this.resources.data.tags[
          this.resources.data.tags.findIndex(elem => elem.tagId === tag.id)
        ]
      );
      // this.$forceUpdate();
    },

    removeTag(tag) {
      this.assetForm.childTags = this.assetForm.childTags.filter(
        elem => elem.id !== tag.id
      );
      this.tagsToDelete.push(tag.id);
      this.orphanTags.push(
        this.resources.data.tags[
          this.resources.data.tags.findIndex(elem => elem.tagId === tag.id)
        ]
      );
      // this.$forceUpdate();
    },
    async replaceTag(tagInfo) {
      try {
        const oldTag = this.resources.data.tags.find(
          tag => tag.id === tagInfo.id
        );

        const lastPosition = oldTag.lastPosition;
        if (!lastPosition) {
          await positionLogsApi.updateTagPosition(tagInfo.id, {
            tagId: this.tagToReplace
          });
        } else {
          await positionLogsApi.updateTagPosition(tagInfo.id, {
            tagId: this.tagToReplace,
            x: lastPosition.x,
            y: lastPosition.y
          });
        }
        this.$notify({
          title: this.$t("statusMessages.success"),
          message:
            tagInfo.type === 0
              ? this.$t("messages.beaconUpdated")
              : this.$t("messages.tagUpdated"),
          type: "success"
        });
        this.updateTagsList();
        this.assetForm = {
          tagId: 0,
          label: "",
          active: false,
          threshold: null,
          battery: -1,
          lastSeen: -1,
          error: "",
          status: "NO STATUS",
          childTags: [],
          beaconId: null,
          destroy: false
        };
        this.selectedChildBeacons = [];
        this.tagsToSend = [];
        this.tagsToDelete = [];
        this.tagToReplace = null;
        this.$emit("update-edit-dialog-visibility", false);
      } catch (err) {
        this.$notify.error({
          title: this.$t("statusMessages.error"),
          message: this.$t("messages.genericError")
        });
      }
    },

    onActiveStatusChange() {
      if (this.isClientDefaultNonTracker && !this.assetForm.active) {
        this.routesAssociatedToTag = this.routeList.filter(route => {
          return route.tags.some(tag => tag.id == this.assetForm.tagId);
        });
        if (this.routesAssociatedToTag.length > 0) {
          this.showAssociatedRoutes = true;
        }
      }
    },
    checkTagsThatAreInRoute() {
      this.resources.data.tags.forEach(tag => {
        tag.inRoute = this.routeList.some(route =>
          route.tags.some(routeTag => routeTag.id === tag.tagId)
        );
      });
    },
    async fetchRouteList() {
      try {
        const res = await routesApi.getRouteList();
        this.routeList = res.data;
        this.checkTagsThatAreInRoute();
      } catch (error) {
        console.log(error);
      }
    },
    updateTempOrphanTags() {
      let totalTags = [];
      if (this.clientAnchors.data) {
        this.clientAnchors.data.forEach(elem => {
          if (elem.activeRoute)
            if (elem.activeRoute.tags)
              totalTags = totalTags.concat(elem.activeRoute.tags);
        });
      } else {
        this.clientAnchors.forEach(elem => {
          if (elem.activeRoute)
            if (elem.activeRoute.tags)
              totalTags = totalTags.concat(elem.activeRoute.tags);
        });
      }
      this.tempOrphanTags = this.resources.data.tags.filter(
        tag =>
          tag.numberOfChildren === 0 &&
          tag.parentId == null &&
          tag.active &&
          !totalTags.some(t => t.tagPosition.tagId === tag.id)
      );
    },
    redirectToDefineLocations() {
      this.assetForm = null;
      this.$router.push({
        name: "user-settings",
        params: {
          tab: "beacons-location",
          edit: true,
          beaconId: this.assetPrevInfo.id
        }
      });
    }
  },
  mounted() {
    window.onresize = () => {
      this.windowWidth = window.innerWidth;
    };
  },
  created() {
    if (this.isClientDefaultNonTracker) {
      this.fetchRouteList();
    }
    this.updateTempOrphanTags();
  }
};
</script>

<style lang="scss" scoped>
.edit-dialog {
  .el-dialog {
    width: 920px !important;

    @media (max-width: $md) {
      width: 98vw !important;
      display: flex;
      flex-direction: column;
    }
  }

  .background-animation {
    -webkit-animation-name: animation;
    -webkit-animation-duration: 4s;
    -webkit-animation-timing-function: ease-in-out;
    -webkit-animation-iteration-count: infinite;
    -webkit-animation-play-state: running;

    animation-name: animation;
    animation-duration: 4s;
    animation-timing-function: ease-in-out;
    animation-iteration-count: infinite;
    animation-play-state: running;
    background-color: white;
    // width: 83%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 3px 10px;
    border-radius: 5px;
  }

  .child-beacon-list {
    display: flex;
    margin-top: 6px;

    .label-div {
      display: flex;
      align-items: center;
    }

    &__id {
      padding: 3px 10px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      width: 100%;
    }

    // &__delete {
    //   float: right;
    //   position: absolute;
    //   right: 10px;
    // }
  }

  .edit-asset-values {
    display: grid;
    grid-template-columns: 33% 34% 33%;
    margin-bottom: 2%;

    .auto-phrase {
      word-break: auto-phrase;
    }
  }

  .el-form-item {
    margin-bottom: 20px;
  }

  .form-item-lg-margin {
    margin-bottom: 40px;
  }

  .form-item-label {
    text-transform: uppercase;
    font-weight: bold;
    font-size: 12px;
    color: #606266;
    text-align: left;
  }

  .edit-asset-form {
    border-top: 1px solid black;
    margin-top: 20px;
    padding: 3% 10px 0;

    @media (max-width: $xs) {
      padding: 3% 5px 0;
    }

    // .edit-asset-range-level {
    //   .el-form-item__label {
    //     padding-top: 10px;
    //   }
    // }

    // .edit-asset-replace {
    //   margin-top: 13%;
    //   display: flex;
    //   align-items: center;

    //   .select-tags {
    //     width: 95%;
    //     margin-left: 32px;
    //   }
    // }

    // .edit-asset-status {
    //   display: flex;
    //   justify-content: left;
    //   align-items: center;
    //   margin-top: 20px;

    //   .el-checkbox {
    //     margin-left: 45%;
    //   }
    //   .select-status {
    //     width: 90%;
    //     margin-left: 32px;
    //   }
    // }

    // .edit-asset-select-tags {
    //   padding-left: 14px;
    //   width: 115%;

    //   @media (max-width: 860px) {
    //     width: 100%;
    //   }
    // }

    // .sm-form-item {
    //   display: grid;
    //   grid-template-columns: 1% 35% 55% 1%;
    //   align-items: center;
    //   justify-items: self-start;
    //   height: min-content;
    //   margin-bottom: 20px;

    //   .el-form-itemcontent {
    //     width: 100% !important;
    //     text-align: left !important;
    //   }
    // }

    .add-tag-btn {
      padding-left: 67px;

      @media (max-width: 860px) {
        padding-left: 22px;
      }
    }

    // .edit-asset-child-tags {
    //   display: grid;
    //   grid-template-columns: 32% 68%;
    //   margin-bottom: 3%;
    // }

    .edit-asset-children {
      display: flex;
      flex-direction: row;
      align-items: center;
    }

    // .edit-asset-label {
    //   display: flex;
    //   justify-content: left;
    //   align-items: center;

    //   .el-input {
    //     margin-left: 12%;
    //   }
    // }
  }

  .test-class {
    display: flex;
  }

  .checkbox-col {
    display: flex;
    align-items: center;
  }

  .slider-col {
    padding: 0 12px;
  }

  .el-select {
    width: 100%;

    .el-input__inner {
      width: 100%;
    }
  }

  .warning-container {
    display: flex;
    align-items: center;
    padding: 8px;
    gap: 8px;
    border-radius: 5px;
    font-size: 16px;
    text-align: justify;
    margin-bottom: 10px;
  }

  .yellow {
    background-color: #fff7cc;
    border: 1px solid #ffeb7f;
  }

  .red {
    background-color: #ffcccc;
    border: 1px solid #ff7f7f;
  }

  .redirect-button-container {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: 20px;
  }
}
</style>
